<script setup>
import BaseDropdown from "../BaseDropdown.vue";
import SvgIcon from "../SvgIcon.vue";

// The action menu used at the end of a home list row: a bordered dots
// trigger that opens a right-aligned dropdown. Pass the menu items
// (`.list-item` <li>s) via the default slot.
// Assumes it sits in a position:relative row (.row-item).
defineProps({
  // Accessible name for the trigger, e.g. `Actions for ${name}`.
  label: { type: String, required: true },
});
</script>

<template>
  <BaseDropdown align="right" class="kebab-menu">
    <template #trigger>
      <button type="button" class="dots" :aria-label="label">
        <SvgIcon name="system/more-vertical" width="16" height="16" />
      </button>
    </template>
    <slot />
  </BaseDropdown>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;
@use "@/styles/palette" as p;
@use "@/styles/mixins/typography";

.kebab-menu {
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto 0;
  inset-inline-end: 6px;
  display: flex;
  align-items: stretch;
  height: 28px;
  border: 1px solid c.$border-subtle;
  border-radius: 6px;
  background: c.$background-ui;
  overflow: hidden;

  &:hover {
    background-color: c.$border-subtle;
  }
}

.dots {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;
  color: p.$brand-50;
  padding: 0;
  outline: revert;
  transition: color 100ms ease;

  &:hover {
    color: c.$main;
  }
}

// Menu items come from the default slot, so they carry the parent's scope.
// Style them through :slotted so the rule lives here, not duplicated per widget.
:slotted(.list-item) {
  @include typography.small;

  display: flex;
  align-items: center;
  padding: 8px 14px;
  cursor: pointer;
  color: c.$main;
  white-space: nowrap;
}

:slotted(.list-item:hover) {
  background: c.$grey-lightest;
}
</style>
